import { authApi } from '@/api/authApi'
import { DEFAULT_DEVICE_KEY_TYPE, type DeviceKeyType } from '@/api/deviceApi'
import type { StreamField } from '@/api/streamsApi'
import { updateCurrentUserName, updateCurrentUserSettings, usersApi } from '@/api/usersApi'
import { AUTH_STORAGE_KEY } from '@/config/storageKeys'
import { queryClient } from '@/lib/queryClient'
import { authKeys } from '@/lib/queryKeys'
import { currentRouteScope, projectPath, stripScopePrefix, toOrgSegment, toTenantSegment } from '@/lib/routeScope'
import type { Permissions } from '@/rbac/grants'
import { isPermissionGrantActive } from '@/rbac/isPermissionGrantActive'
import { forgetStreamsCacheScope } from '@/stores/streamsStore'
import { isOwner } from '@/utils/permissions'
import { create } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import {
	type ApiError,
	type AuthStore,
	type ConsoleUser,
	DEFAULT_SERIAL_KEY_LABEL,
	type LoginCredentials,
	type OrgFrontendSettings,
	type PlatformUser,
	findUserOrganization,
	findUserTenant,
	isConsoleUser,
	isPlatformUser,
	mergeDisplayPreferences,
	redirectToLoginIf401,
} from './auth'

interface AuthState {
	user: ConsoleUser | PlatformUser | null
	userType: 'console' | 'platform' | null
	currentTenant: string
	recentTenants: string[]
	isAuthenticated: boolean
	isLoading: boolean
	error: string | null
	lastUsedProvider: string | null // Added this to store the last used OAuth provider
	currentOrganizationId: string | null // Current organization context for billing/quota
}

const MAX_RECENT_TENANTS = 3

// Serializes updateUserSettingsWith calls so each one reads settings only after all earlier
// writes have applied, not off a stale snapshot — prevents two near-simultaneous writes from
// overwriting each other (PATCH /v3/me replaces the whole settings JSON).
let settingsWriteQueue: Promise<void> = Promise.resolve()

const initialState: AuthState = {
	user: null,
	userType: null,
	currentTenant: '', // Empty string when no user is logged in
	recentTenants: [],
	isLoading: false,
	isAuthenticated: false,
	error: null,
	lastUsedProvider: null, // set last used provider
	currentOrganizationId: null,
}

export const useAuthStore = create<AuthStore>()(
	devtools(
		persist(
			subscribeWithSelector<AuthStore>((set, get) => ({
				// Initial state
				...initialState,

				// Authentication actions
				login: async (credentials: LoginCredentials) => {
					set({ isLoading: true, error: null, lastUsedProvider: credentials.provider })

					try {
						// Use provided redirect URI or default to '/' (indexRoute will handle appropriate redirect)
						const redirectUri = credentials.redirectUri ?? '/'

						// Redirect to initiate-login endpoint
						authApi.initiateLogin(credentials.provider, redirectUri)
					} catch (error: unknown) {
						const apiError = error as ApiError
						set({
							isLoading: false,
							error: apiError.message || 'Login failed',
						})
						throw error
					}
				},

				logout: async () => {
					try {
						// Clear local state before redirecting
						set({
							user: null,
							userType: null,
							currentTenant: '',
							recentTenants: [],
							isLoading: false,
							isAuthenticated: false,
							error: null,
							lastUsedProvider: null,
							currentOrganizationId: null,
						})
						// The next user may land on the same project with different permissions.
						forgetStreamsCacheScope()
						await authApi.logout()
					} catch (error) {
						console.error('Failed to logout cleanly:', error)
					}
				},

				refreshUser: async () => {
					set({ isLoading: true, error: null })

					// Scope /me to the active tenant explicitly so the returned role matches the current
					// project rather than whatever tenant is in the URL. Held for the comparison below.
					const requestedTenant = get().currentTenant

					try {
						const user = await usersApi.getCurrentUser(requestedTenant || undefined)

						// A switch that lands while this is in flight has already asked for the role of the
						// project now on screen. `/v3/me` is tenant scoped, so writing this response would put
						// the previous project's role behind the new project's pages until the next refresh.
						if (get().currentTenant !== requestedTenant) {
							set({ isLoading: false })
							return { user }
						}

						const userType: 'console' | 'platform' = isConsoleUser(user) ? 'console' : 'platform'
						const currentOrgId: string | null = get().currentOrganizationId || user.organizations?.[0]?.id || null

						// This refetch (fired on every tenant switch) can race ahead of an in-flight
						// debounced display-preference write; merge instead of replacing so it isn't
						// silently dropped.
						const previousUser = get().user
						const mergedUser =
							isPlatformUser(user) && isPlatformUser(previousUser)
								? {
										...user,
										settings: {
											...user.settings,
											displayPreferences: mergeDisplayPreferences(
												previousUser.settings?.displayPreferences,
												user.settings?.displayPreferences,
											),
										},
									}
								: user

						set({
							user: mergedUser,
							userType,
							currentOrganizationId: currentOrgId,
							isLoading: false,
							isAuthenticated: true,
							error: null,
						})

						// Keep React Query cache for currentUser in sync with the latest /me response
						queryClient.setQueriesData({ queryKey: authKeys.currentUser() }, () => mergedUser)

						// Set current tenant if not already set (e.g., on initial login).
						// Don't overwrite if already set — switchTenant sets it explicitly.
						if (isPlatformUser(user) && user.tenant_id && !get().currentTenant) {
							set({ currentTenant: user.tenant_id })
						}

						return { user: mergedUser }
					} catch (error: unknown) {
						if (redirectToLoginIf401(error, get().clearAuthState)) {
							throw error
						}
						const apiError = error as ApiError
						// Network blips and 5xx are transient — dropping the user/tenant/org here blanks
						// the switcher mid-session. Only an access error means the session really changed,
						// and only for the tenant still on screen: a 403 from the project the user just left
						// says nothing about the one they moved to, and clearing on it signs them out.
						const lostAccess =
							apiError?.status >= 400 && apiError?.status < 500 && get().currentTenant === requestedTenant
						set({
							isLoading: false,
							error: apiError.message || 'Failed to refresh user',
							// Same fields clearAuthState drops, minus `error` — the message is what this branch
							// exists to keep. isAuthenticated must go with the user: authenticatedRouteGuard reads
							// only that flag, so leaving it set admits the session with no user behind it.
							...(lostAccess && {
								user: null,
								userType: null,
								currentTenant: '',
								recentTenants: [],
								currentOrganizationId: null,
								isAuthenticated: false,
							}),
						})
						throw error
					}
				},

				// User management actions
				setUser: (user: ConsoleUser | PlatformUser | null) => {
					const currentState = get()
					if (currentState.isAuthenticated && !user) {
						return
					}

					const userType: 'console' | 'platform' = isConsoleUser(user) ? 'console' : 'platform'

					// A /me refetch (e.g. on tenant switch) can race ahead of an in-flight debounced
					// display-preference write; merge instead of replacing so it isn't silently dropped.
					const mergedUser =
						isPlatformUser(user) && isPlatformUser(currentState.user)
							? {
									...user,
									settings: {
										...user.settings,
										displayPreferences: mergeDisplayPreferences(
											currentState.user.settings?.displayPreferences,
											user.settings?.displayPreferences,
										),
									},
								}
							: user

					set({
						user: mergedUser,
						userType,
						isAuthenticated: !!user,
						// Preserve currentTenant if already set (e.g., after switchTenant).
						// Only fall back to user.tenant_id on initial login when currentTenant is empty.
						currentTenant: currentState.currentTenant || (isPlatformUser(user) ? user.tenant_id || '' : ''),
						// Preserve recentTenants — don't reset on every setUser call
						recentTenants:
							currentState.recentTenants.length > 0
								? currentState.recentTenants
								: isPlatformUser(user) && user.tenant_id
									? [user.tenant_id]
									: [],
						// Preserve currentOrganizationId if already set
						currentOrganizationId: currentState.currentOrganizationId || user?.organizations?.[0]?.id || null,
					})
				},

				updateUserSettingsWith: updater => {
					const task = async () => {
						const user = get().user
						if (!user || !isPlatformUser(user)) {
							throw new Error('User settings are only available for platform users')
						}

						// PATCH /v3/me replaces the whole settings JSON, so keys explicitly set to
						// undefined are removed rather than sent through.
						const next = updater(user.settings ?? {})
						const merged: Record<string, unknown> = { ...next }
						for (const key of Object.keys(merged)) {
							if (merged[key] === undefined) {
								delete merged[key]
							}
						}
						const settings = merged as PlatformUser['settings']

						await updateCurrentUserSettings(settings ?? {})

						set(state => {
							if (!state.user || !isPlatformUser(state.user)) {
								return {}
							}
							return { user: { ...state.user, settings } }
						})
					}

					// Chain onto the queue regardless of the previous task's outcome, so one
					// rejected write doesn't wedge every write queued after it.
					const result = settingsWriteQueue.then(task, task)
					settingsWriteQueue = result.then(
						() => undefined,
						() => undefined,
					)
					return result
				},

				updateUserSettings: patch => get().updateUserSettingsWith(current => ({ ...current, ...patch })),

				updateUserName: async (name: string) => {
					const user = get().user
					if (!user || !isPlatformUser(user)) {
						throw new Error('User name is only available for platform users')
					}

					await updateCurrentUserName(name)

					set(state => {
						if (!state.user || !isPlatformUser(state.user)) {
							return {}
						}
						return { user: { ...state.user, name } }
					})
				},

				updateOrgOwners: (orgId: string, owners: string[]) => {
					set(state => {
						if (!state.user) {
							return {}
						}
						let updated = false
						let newOrganizations = state.user.organizations
						if (newOrganizations) {
							newOrganizations = newOrganizations.map(org => {
								if (org.id === orgId) {
									updated = true
									return { ...org, owners }
								}
								return org
							})
						}
						let newTenantList = isPlatformUser(state.user) ? state.user.tenant_list : undefined
						if (newTenantList) {
							newTenantList = newTenantList.map(t => {
								if (t.org_id === orgId && t.organization) {
									updated = true
									return { ...t, organization: { ...t.organization, owners } }
								}
								return t
							})
						}
						if (!updated) {
							return {}
						}
						return {
							user: {
								...state.user,
								...(newOrganizations && { organizations: newOrganizations }),
								...(newTenantList && { tenant_list: newTenantList }),
							},
						}
					})
				},

				updateOrgFrontendSettings: (orgId: string, frontendSettings: OrgFrontendSettings) => {
					set(state => {
						if (!state.user?.organizations) {
							return {}
						}
						let updated = false
						const organizations = state.user.organizations.map(org => {
							if (org.id !== orgId) {
								return org
							}
							updated = true
							return { ...org, frontend_settings: frontendSettings }
						})
						if (!updated) {
							return {}
						}
						return { user: { ...state.user, organizations } }
					})
				},

				setCurrentTenant: (tenant: string) => {
					set({ currentTenant: tenant })
				},

				clearCurrentTenantUnlessBelongsToOrg: (orgId: string) => {
					const { user, currentTenant } = get()
					if (!currentTenant || !orgId) {
						return
					}
					// No user yet means membership is unknown, not false — and currentTenant is persisted,
					// so clearing here loses the selection for good.
					if (!user) {
						return
					}
					if (!isPlatformUser(user)) {
						set({ currentTenant: '' })
						return
					}
					if (user.tenant_list?.length) {
						const belongs = user.tenant_list.some(t => t.id === currentTenant && t.org_id === orgId)
						if (!belongs) {
							set({ currentTenant: '' })
						}
						return
					}
					if (!user.tenants?.includes(currentTenant)) {
						set({ currentTenant: '' })
					}
				},

				clearScopeForDeletedOrg: (orgId: string) => {
					const { user, currentTenant, currentOrganizationId } = get()
					if (!orgId) {
						return
					}
					const next: Partial<AuthState> = {}
					if (currentOrganizationId === orgId) {
						next.currentOrganizationId = null
					}
					if (currentTenant) {
						if (!user || !isPlatformUser(user)) {
							next.currentTenant = ''
						} else if (user.tenant_list?.length) {
							if (user.tenant_list.some(t => t.id === currentTenant && t.org_id === orgId)) {
								next.currentTenant = ''
							}
						} else {
							// No tenant→org mapping available; clear so /me is not scoped to a deleted tenant
							next.currentTenant = ''
						}
					}
					if (Object.keys(next).length > 0) {
						set(next)
					}
				},

				setCurrentOrganizationId: (organizationId: string | null) => {
					set({ currentOrganizationId: organizationId })
				},

				// Recent tenants management
				addRecentTenant: (tenantId: string) => {
					if (!tenantId || typeof tenantId !== 'string') {
						console.warn('Invalid project ID provided to addRecentTenant:', tenantId)
						return
					}

					const { recentTenants } = get()

					// Remove tenant if it already exists (to avoid duplicates)
					const filtered = recentTenants.filter(id => id !== tenantId)

					// Add to front of array
					const updated = [tenantId, ...filtered]

					// Limit to maximum number of recent tenants
					const limited = updated.slice(0, MAX_RECENT_TENANTS)

					set({ recentTenants: limited })
				},

				// Tenant switching
				switchTenant: (tenantId: string, navigate?: (options: { to: string; replace?: boolean }) => void) => {
					const { user } = get()
					const scope = currentRouteScope()
					// Only skip when we're already ON a project route for this tenant. At org level (no tenant in
					// the URL), navigate.
					if (scope.tenantId === tenantId) {
						return
					}

					// Only platform users can switch tenants
					if (!isPlatformUser(user)) {
						return
					}

					// Validate tenant access - users need direct access or owner privileges
					const canAccess = user.tenants?.includes(tenantId) || isOwner()
					if (!canAccess) {
						console.error(`Cannot switch to project "${tenantId}": insufficient permissions`)
						return
					}

					try {
						// The URL org is only right for the org on screen; a project picked from another org needs its own.
						const orgId = findUserTenant(user, tenantId)?.org_id || scope.orgId || get().currentOrganizationId

						if (!orgId) {
							console.error('Cannot switch project: orgId not available')
							return
						}

						// Keep the page the user is on, when they are on one inside a project.
						let pagePath = scope.tenantId ? stripScopePrefix(window.location.pathname) : '/'
						// A specific dashboard resets to the list: dashboard ids are tenant-specific and would
						// 404 in the new tenant.
						if (pagePath.startsWith('/data/dashboards/view/')) {
							pagePath = '/data/dashboards/all'
						}
						const newPath = projectPath(
							toOrgSegment(orgId),
							toTenantSegment(tenantId),
							pagePath === '/' ? '' : pagePath,
						)

						// Update tenant state first to ensure any refetches use the new tenant
						get().addRecentTenant(tenantId)
						set({ currentTenant: tenantId, currentOrganizationId: orgId })

						// Clear query cache to fetch fresh data for the new tenant
						queryClient.clear()

						// Clear non-auth localStorage keys on tenant switch. This also purges any
						// `app-custom-views` entries left behind by the removed custom views feature.
						const keysToRemove = Object.keys(localStorage).filter(key => key !== AUTH_STORAGE_KEY)
						for (const key of keysToRemove) {
							localStorage.removeItem(key)
						}

						if (navigate) {
							navigate({ to: newPath, replace: true })
						} else {
							import('@/app/routes/routes').then(({ router }) => {
								router.navigate({ to: newPath })
							})
						}
					} catch (error) {
						console.error('Failed to switch project:', error)
					}
				},

				// State management actions
				setLoading: (loading: boolean) => {
					set({ isLoading: loading })
				},

				setError: (error: string | null) => {
					set({ error })
				},

				clearError: () => {
					set({ error: null })
				},

				clearAuthState: () => {
					// Clear auth state when 401 occurs (called by interceptors)
					// Router will handle redirect based on auth state change
					// The router's beforeLoad will check auth state on next navigation
					set({
						user: null,
						userType: null,
						currentTenant: '',
						recentTenants: [],
						isAuthenticated: false,
						isLoading: false,
						error: null,
						currentOrganizationId: null,
					})
				},

				setLastUsedProvider: (provider: string) => {
					set({ lastUsedProvider: provider })
				},

				// Utility actions
				hasPermission: (permission: keyof Permissions): boolean => {
					const { user } = get()
					if (!isPlatformUser(user) || !user.role?.permissions) {
						return false
					}

					return isPermissionGrantActive(user.role.permissions[permission])
				},

				hasRole: (roleName: string): boolean => {
					const { user } = get()
					if (!isPlatformUser(user)) {
						return false
					}
					return user.role?.name === roleName
				},

				getTenantRole: (tenantId: string): string | null => {
					const { user } = get()
					if (!isPlatformUser(user)) {
						return null
					}

					if (!user['tenant-roles']) {
						return user.role?.name || null
					}

					const tenantRole = user['tenant-roles'].find(tr => tr.tenant_id === tenantId)
					return tenantRole?.name || user.role?.name || null
				},

				canAccessTenant: (tenantId: string): boolean => {
					const { user } = get()
					if (isPlatformUser(user)) {
						return user.tenants?.includes(tenantId) || isOwner()
					}
					return false
				},
			})),
			{
				name: 'auth-storage',
				partialize: state => ({
					user: state.user,
					userType: state.userType,
					currentTenant: state.currentTenant,
					recentTenants: state.recentTenants,
					lastUsedProvider: state.lastUsedProvider,
					currentOrganizationId: state.currentOrganizationId,
				}),
				// Only persist non-sensitive data
				onRehydrateStorage: () => (state: AuthStore | undefined) => {
					if (state) {
						// Recalculate isAuthenticated based on persisted user
						state.isAuthenticated = !!state.user
						if (state.user && !state.userType) {
							state.userType = isConsoleUser(state.user) ? 'console' : 'platform'
						}
						if (state.user && !state.currentTenant) {
							state.currentTenant = isPlatformUser(state.user) ? state.user.tenant_id || '' : ''
						}
					}
				},
			},
		),
		{
			name: 'auth-store',
		},
	),
)

// Selectors for better performance
export const useUser = () => useAuthStore(state => state.user)
export const useCurrentTenant = () => useAuthStore(state => state.currentTenant)
export const useRecentTenants = () => useAuthStore(state => state.recentTenants)
export const useSwitchTenant = () => useAuthStore(state => state.switchTenant)
export const useAddRecentTenant = () => useAuthStore(state => state.addRecentTenant)
export const useIsAuthenticated = () => useAuthStore(state => state.isAuthenticated)
export const useIsLoading = () => useAuthStore(state => state.isLoading)
export const useAuthError = () => useAuthStore(state => state.error)
export const useGetTenantRole = () => useAuthStore(state => state.getTenantRole)

// Permission selectors
export const useHasPermission = (permission: keyof Permissions) =>
	useAuthStore(state => state.hasPermission(permission))

export const useHasRole = (roleName: string) => useAuthStore(state => state.hasRole(roleName))

export const useCanAccessTenant = (tenantId: string) => useAuthStore(state => state.canAccessTenant(tenantId))

// Billing/subscription selectors
export const useCurrentOrganizationIdFromState = () => useAuthStore(state => state.currentOrganizationId)

/** Always a usable label: a project without settings falls back to the same default a new one gets. */
export const useSerialKeyLabel = () =>
	useAuthStore(state => {
		if (!isPlatformUser(state.user)) {
			return DEFAULT_SERIAL_KEY_LABEL
		}
		return state.user['tenant-settings']?.frontend_settings?.serial_key || DEFAULT_SERIAL_KEY_LABEL
	})

export const useOrgDeviceKeyType = (orgId: string | null | undefined): DeviceKeyType =>
	useAuthStore(
		state => findUserOrganization(state.user, orgId)?.frontend_settings?.device_key_type ?? DEFAULT_DEVICE_KEY_TYPE,
	)

const NO_DEFAULT_COLUMNS: StreamField[] = []

/** Columns configured for the current project. The backend adds them to every stream it creates. */
export const useProjectDefaultColumns = () =>
	useAuthStore(state => {
		if (!isPlatformUser(state.user)) {
			return NO_DEFAULT_COLUMNS
		}
		const columns = state.user['tenant-settings']?.frontend_settings?.default_columns
		return Array.isArray(columns) ? columns : NO_DEFAULT_COLUMNS
	})

// Action selectors
// Use useShallow to prevent infinite loops by doing shallow comparison of the returned object
export const useAuthActions = () =>
	useAuthStore(
		useShallow(state => ({
			login: state.login,
			logout: state.logout,
			refreshUser: state.refreshUser,
			setUser: state.setUser,
			setCurrentTenant: state.setCurrentTenant,
			setCurrentOrganizationId: state.setCurrentOrganizationId,
			setLoading: state.setLoading,
			setError: state.setError,
			clearError: state.clearError,
			setLastUsedProvider: state.setLastUsedProvider,
			clearAuthState: state.clearAuthState,
		})),
	)

/*
 * Subscription: refresh what a tenant switch invalidates.
 *
 * `/v3/me` is not fetched here. `useCurrentUser` keys that query on `currentTenant` and writes the
 * result through `setUser`, which merges display preferences and preserves the tenancy fields exactly
 * as `refreshUser` does, so calling `refreshUser` here issued a second request for the tenant React
 * Query was already fetching. Invalidating instead keeps the switch authoritative over a cached role
 * without racing a request against it.
 */
let prevTenant: string = useAuthStore.getState().currentTenant
useAuthStore.subscribe(
	state => ({
		currentTenant: state.currentTenant,
		isAuthenticated: state.isAuthenticated,
		user: state.user,
	}),
	({ currentTenant, isAuthenticated, user }) => {
		// Only refresh on actual tenant-to-tenant switches:
		// - Skip if not authenticated (logout clears tenant)
		// - Skip if tenant is empty (clearing state)
		// - Skip if previous tenant was empty (initial load / login)
		// - Skip if tenant didn't actually change
		if (!isAuthenticated || !currentTenant || !prevTenant || currentTenant === prevTenant) {
			prevTenant = currentTenant
			return
		}

		// Skip if user is not a member of the tenant (e.g. Org Owner accessing via overriding permissions):
		// the quota endpoint is owner-only and answers for the organization, not this project.
		if (isPlatformUser(user) && !user.tenants?.includes(currentTenant)) {
			prevTenant = currentTenant
			return
		}

		prevTenant = currentTenant

		// The role in `/v3/me` is per project, so the cached answer for the project being entered must
		// not outlive the switch onto it. Scoped to that one key: invalidating the whole `currentUser`
		// family also refetches the project being left, which is the duplicate this removed.
		queryClient.invalidateQueries({ queryKey: [...authKeys.currentUser(), currentTenant] })

		// Refresh quota usage for the new tenant so access-control hooks have fresh data.
		// Dynamic import avoids circular dependency between authStore and quotaStore.
		import('./quotaStore')
			.then(({ useQuotaStore }) => useQuotaStore.getState().refreshQuotaUsage(currentTenant))
			.catch(err => {
				console.error('Failed to refresh quota usage after tenant switch:', err)
			})
	},
	{ equalityFn: (a, b) => a.currentTenant === b.currentTenant && a.isAuthenticated === b.isAuthenticated },
)
